import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';
type VercelAccent = 'blue' | 'violet' | 'pink' | 'auto';

interface VercelThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultAccent?: VercelAccent;
  storageKey?: string;
}

interface VercelThemeProviderState {
  theme: Theme;
  accent: VercelAccent;
  setTheme: (theme: Theme) => void;
  setAccent: (accent: VercelAccent) => void;
}

const initialState: VercelThemeProviderState = {
  theme: 'system',
  accent: 'blue',
  setTheme: () => null,
  setAccent: () => null,
};

const VercelThemeProviderContext = createContext<VercelThemeProviderState>(initialState);

export function VercelThemeProvider({
  children,
  defaultTheme = 'system',
  defaultAccent = 'blue',
  storageKey = 'vite-ui-theme',
  ...props
}: VercelThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  
  const [accent, setAccent] = useState<VercelAccent>(
    () => (localStorage.getItem(`${storageKey}-accent`) as VercelAccent) || defaultAccent
  );

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    // Apply Vercel accent color system
    root.style.setProperty('--vercel-active-accent', 
      accent === 'blue' ? 'var(--vercel-bright-blue)' :
      accent === 'violet' ? 'var(--vercel-electric-violet)' :
      accent === 'pink' ? 'var(--vercel-vivid-pink)' :
      'var(--vercel-bright-blue)' // auto fallback
    );

    // Update primary accent to match selection
    root.style.setProperty('--primary', 
      accent === 'blue' ? 'var(--vercel-bright-blue)' :
      accent === 'violet' ? 'var(--vercel-electric-violet)' :
      accent === 'pink' ? 'var(--vercel-vivid-pink)' :
      'var(--vercel-bright-blue)' // auto fallback
    );

    // Update chart colors to match accent
    if (accent === 'violet') {
      root.style.setProperty('--chart-1', 'var(--vercel-electric-violet)');
      root.style.setProperty('--chart-2', 'var(--vercel-french-violet)');
    } else if (accent === 'pink') {
      root.style.setProperty('--chart-1', 'var(--vercel-vivid-pink)');
      root.style.setProperty('--chart-2', 'var(--vercel-orange-red)');
    } else {
      root.style.setProperty('--chart-1', 'var(--vercel-bright-blue)');
      root.style.setProperty('--chart-2', 'var(--vercel-electric-violet)');
    }
  }, [theme, accent]);

  const value = {
    theme,
    accent,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    setAccent: (accent: VercelAccent) => {
      localStorage.setItem(`${storageKey}-accent`, accent);
      setAccent(accent);
    },
  };

  return (
    <VercelThemeProviderContext.Provider {...props} value={value}>
      {children}
    </VercelThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(VercelThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a VercelThemeProvider');

  return context;
};
